import { createTheme, ThemeProvider } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React from "react";
import { useLanguageTheme } from "../../../../hooks/useLanguageTheme";
import { affiliatesTableColumns } from "../contants";
import {
  AffiliatesApiData,
  convertedAffiliatesData,
  IAffiliatesColumn,
} from "../interfaces";
import {
  extractNumberFromCurrency,
  formatDateDMY,
  transformAffiliatesApiDataToTableData,
} from "../utils";
import {
  createTableTheme,
  StyledTableContainer,
  StyledTotalRow,
} from "./AffiliatesStatisticsTableStyles";
import { ISortParams } from "../../../portfolio/interfaces";
import { useTranslation } from "react-i18next";
import { NoAffiliatesFound } from "./NoAffiliatesFound";
import { formatPrice } from "../../../../helpers/dataConverts";

interface TotalRowProps {
  allRows: convertedAffiliatesData[];
  columns: readonly IAffiliatesColumn[];
}

const TotalRow: React.FC<TotalRowProps> = ({ allRows, columns }) => {
  const totalEarned = allRows?.reduce(
    (sum, row) => sum + extractNumberFromCurrency(row.earned),
    0
  );
  const totalPending = allRows?.reduce(
    (sum, row) => sum + extractNumberFromCurrency(row.pending),
    0
  );

  return (
    <StyledTotalRow>
      {columns.map((column) => {
        let value: string | number = "";
        if (column.id === "leadName") {
          value = `Total: ${allRows?.length ? allRows?.length : 0}`;
        } else if (column.id === "earned") {
          value = `₪${formatPrice(totalEarned)}`;
        } else if (column.id === "pending") {
          value = `₪${formatPrice(totalPending)}`;
        }
        return (
          <TableCell key={column.id} align={column.align}>
            {value}
          </TableCell>
        );
      })}
    </StyledTotalRow>
  );
};

export const AffiliatesStatisticsTable = ({
  data,
  setSort,
}: {
  data: AffiliatesApiData[];
  setSort: React.Dispatch<React.SetStateAction<ISortParams>>;
}) => {
  const { i18n, t } = useTranslation(["profile"]);
  const currentLanguage = i18n.language;
  const langTheme = useLanguageTheme();
  const isRTL = i18n.dir() === "rtl";

  const theme = createTheme(createTableTheme(langTheme, isRTL));

  const rows = transformAffiliatesApiDataToTableData(data);
  const hasData = rows?.length > 0;

  const handleSetSort = (columnId: string) => {
    setSort((prevSort) => ({
      field: columnId,
      direction:
        prevSort.field === columnId && prevSort.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const columns = affiliatesTableColumns.map((column) => {
    if (column.id === "registrationDate") {
      return {
        ...column,
        format: (value: string) => formatDateDMY(value, currentLanguage),
      };
    }
    if (column.id === "earned" || column.id === "pending") {
      return {
        ...column,
        format: (value: string) => formatPrice(value),
      };
    }
    return column;
  });

  return (
    <div
      className="affiliates-table-wrapper"
      style={{
        direction: i18n.dir(),
      }}
    >
      <ThemeProvider theme={theme}>
        <StyledTableContainer hasData={hasData}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns?.map((column, index) => (
                  <TableCell
                    onClick={
                      index !== 0 ? () => handleSetSort(column.id) : undefined
                    }
                    key={column.id}
                    align={column.align}
                    style={{
                      minWidth: column.minWidth,
                      cursor: index !== 0 ? "pointer" : "auto",
                    }}
                  >
                    {t(column.label)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows?.map((row) => (
                <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                  {columns?.map((column) => {
                    const value = row[column.id];
                    return (
                      <TableCell key={column.id} align={column.align}>
                        {column.renderCell
                          ? column.renderCell(value)
                          : column.format
                          ? column.format(value as any)
                          : value}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
              {rows?.length > 0 && (
                <TotalRow allRows={rows} columns={affiliatesTableColumns} />
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </ThemeProvider>

      {rows?.length === 0 && <NoAffiliatesFound />}
    </div>
  );
};
